// @flow
import * as React from 'react';
import { Modal } from '@ta/overlays.modal';
import { Closer, Opener } from '@ta/overlays.managers';
import { useSocialLoginGate } from '@ta/social.login-gate';
import { TitleBar } from '@ta/modal.headers';
import { localize } from '@ta/common.i18n';
import UnblockProfile from './UnblockProfile';
import ConfirmationView from './ConfirmationView';
import UnblockUserDescriptionContent from './UnblockUserDescriptionContent';

type DisplayProps = {|
  /** The display name of the user */
  displayName: string,
|};

type ModalProps = {|
  /** A namespace passed in to deduplicate modals */
  namespace: string,
|};

type ControlFlowProps = {|
  /** An optional callback whenever the modal is closed */
  onClose?: () => void,
  /** An optional callback when the the submission completes */
  onSubmit?: () => void,
|};

type ExternalProps = {|
  ...DisplayProps,
  ...ModalProps,
  ...ControlFlowProps,
  /** The corresponding userId to block */
  userIdToUnblock: string,
|};

type Props = {|
  /** Props necessary for display */
  ...DisplayProps,
  /** An optional callback when the the submission completes */
  onSubmit?: () => void,
  /** Internal: If true, the modal is attempting to submit the mutation. Provided by <UnblockProfile/>  */
  isSubmitting: boolean,
  /** Internal: A function that resolves a promise to unblock a profile.Provided by <UnblockProfile/> */
  unblockProfile: () => Promise<*>,
  /** Internal: A function to close the modal. Provided by <Closer/> */
  closeModal: () => mixed,
|};

type OpenModalProps = {|
  /** userId that the modal will correspond to */
  userId: string,
  /** A namespace passed in to deduplicate modals */
  namespace: string,
  /** A render prop that gives you an open function */
  children: (open: () => mixed) => React.Node,
|};

/** View of the Unblock modal. Exported for testing purposes only */
export class UnblockUserView extends React.Component<Props> {
  onSubmit = () => {
    const { unblockProfile, closeModal, onSubmit } = this.props;
    // A successful mutation will close the modal. Unsuccessful mutations will throw an error, and not close the modal
    unblockProfile().then(() => {
      closeModal();
      if (typeof onSubmit === 'function') {
        onSubmit();
      }
    });
  };

  render() {
    const { displayName, isSubmitting, closeModal } = this.props;
    return (
      <ConfirmationView
        descriptionContent={<UnblockUserDescriptionContent displayName={displayName} />}
        onSubmit={this.onSubmit}
        onCancel={closeModal}
        /** Unblock */
        submitContent={localize('social_unblock_btn')}
        isSubmitting={isSubmitting}
      />
    );
  }
}

/** Makes a name from a namespace and userId */
export const toName = (namespace: string, userId: string) => `${namespace}:UNBLOCK_USER:${userId}`;

/** Opens an unblock user modal with the specified user id */
export const OpenUnblockUserModal = ({ userId, namespace, children }: OpenModalProps) => {
  const socialGate = useSocialLoginGate({});
  return (
    <Opener name={toName(namespace, userId)}>
      {open =>
        children(() => {
          socialGate().then(open);
        })
      }
    </Opener>
  );
};

OpenUnblockUserModal.defaultProps = { namespace: '' };

const WrappedUnblockUserModal = ({
  userIdToUnblock,
  displayName,
  namespace,
  onClose,
  onSubmit,
}: ExternalProps) => (
  <Closer>
    {close => (
      <Modal
        name={toName(namespace, userIdToUnblock)}
        header={
          <TitleBar displayType="CENTERED_WHITE">
            {/** Unblock {displayName}  */}
            {localize('social_unblock_modal_title', { displayName })}
          </TitleBar>
        }
        onClose={onClose}
      >
        <UnblockProfile userIdToUnblock={userIdToUnblock}>
          {(unblockProfile, { loading, data }) => (
            <UnblockUserView
              unblockProfile={unblockProfile}
              // A view is still considered "submitting" even if we have finished so it doesn't leave weird artifacts on submit
              isSubmitting={loading || data}
              displayName={displayName}
              closeModal={close}
              onSubmit={onSubmit}
            />
          )}
        </UnblockProfile>
      </Modal>
    )}
  </Closer>
);

WrappedUnblockUserModal.defaultProps = {
  namespace: '',
};

export default WrappedUnblockUserModal;
